import { MatrixData } from '../components/MatrixConfigurator'
interface MatrixConfig {
  centerX: number
  centerY: number
  outerRadius: number
  marginSize: number
}
function getTextColor(bgColor: string): string {
  const num = parseInt(bgColor.replace('#', ''), 16)
  const r = (num >> 16) & 0xff
  const g = (num >> 8) & 0xff
  const b = num & 0xff
  const brightness = (r * 299 + g * 587 + b * 114) / 1000
  return brightness > 155 ? '#333333' : '#FFFFFF'
}
export function drawMatrix(
  ctx: CanvasRenderingContext2D,
  matrixData: MatrixData,
) {
  const config: MatrixConfig = {
    centerX: 200,
    centerY: 200,
    outerRadius: 180,
    marginSize: 4,
  }
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
  const baseColor = matrixData.color || '#E8927C'
  const textColor = getTextColor(baseColor)
  const workingRadius = config.outerRadius - config.marginSize
  ctx.fillStyle = baseColor
  if (matrixData.supportType === 'square') {
    const size = config.outerRadius * 1.4
    ctx.fillRect(
      config.centerX - size / 2,
      config.centerY - size / 2,
      size,
      size,
    )
  } else {
    ctx.beginPath()
    ctx.arc(config.centerX, config.centerY, config.outerRadius, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.shadowColor = 'rgba(0, 0, 0, 0.15)'
  ctx.shadowBlur = 10
  ctx.shadowOffsetX = 3
  ctx.shadowOffsetY = 3
  ctx.shadowColor = 'transparent'
  ctx.shadowBlur = 0
  ctx.shadowOffsetX = 0
  ctx.shadowOffsetY = 0
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.15)'
  ctx.lineWidth = 1
  ctx.setLineDash([3, 3])
  if (matrixData.supportType === 'square') {
    const size = workingRadius * 1.4
    ctx.strokeRect(
      config.centerX - size / 2,
      config.centerY - size / 2,
      size,
      size,
    )
  } else {
    ctx.beginPath()
    ctx.arc(config.centerX, config.centerY, workingRadius, 0, Math.PI * 2)
    ctx.stroke()
  }
  ctx.setLineDash([])
  drawAllTextZones(ctx, config, matrixData, textColor)
  drawCenterMark(ctx, config, matrixData.centerMark, textColor)
}
function drawAllTextZones(
  ctx: CanvasRenderingContext2D,
  config: MatrixConfig,
  matrixData: MatrixData,
  textColor: string,
) {
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  const shadowColor =
    textColor === '#FFFFFF' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(139, 69, 52, 0.6)'
  matrixData.textZones.forEach((zone) => {
    if (!zone.text) return
    const fontSize = zone.fontSize || 12
    ctx.font = `bold ${fontSize}px Arial, sans-serif`
    const angleInRadians = (zone.angle * Math.PI) / 180
    ctx.fillStyle = shadowColor
    drawCircularText(
      ctx,
      zone.text,
      config.centerX,
      config.centerY + 1,
      zone.radius,
      angleInRadians,
      zone.isInverted,
    )
    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)'
    drawCircularText(
      ctx,
      zone.text,
      config.centerX,
      config.centerY - 0.5,
      zone.radius,
      angleInRadians,
      zone.isInverted,
    )
    ctx.fillStyle = textColor
    drawCircularText(
      ctx,
      zone.text,
      config.centerX,
      config.centerY,
      zone.radius,
      angleInRadians,
      zone.isInverted,
    )
  })
}
function drawCircularText(
  ctx: CanvasRenderingContext2D,
  text: string,
  centerX: number,
  centerY: number,
  radius: number,
  startAngle: number,
  isInverted: boolean = false,
) {
  if (!text) return
  const displayText = isInverted ? text.split('').reverse().join('') : text
  const charWidths: number[] = []
  let totalWidth = 0
  for (let i = 0; i < displayText.length; i++) {
    const width = ctx.measureText(displayText[i]).width
    charWidths.push(width)
    totalWidth += width
  }
  const totalAngle = totalWidth / radius
  let angle = startAngle - totalAngle / 2
  for (let i = 0; i < displayText.length; i++) {
    const char = displayText[i]
    const charWidth = charWidths[i]
    const angleStep = charWidth / radius
    const charAngle = angle + angleStep / 2
    const x = centerX + Math.cos(charAngle) * radius
    const y = centerY + Math.sin(charAngle) * radius
    ctx.save()
    ctx.translate(x, y)
    const rotation = charAngle + Math.PI / 2 + (isInverted ? Math.PI : 0)
    ctx.rotate(rotation)
    ctx.fillText(char, 0, 0)
    ctx.restore()
    angle += angleStep
  }
}
function drawCenterMark(
  ctx: CanvasRenderingContext2D,
  config: MatrixConfig,
  mark: string,
  textColor: string,
) {
  if (mark === 'none') return
  const size = 15
  const shadowColor =
    textColor === '#FFFFFF' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(139, 69, 52, 0.6)'
  ctx.strokeStyle = shadowColor
  ctx.fillStyle = shadowColor
  ctx.lineWidth = 2
  drawMarkShape(ctx, config, mark, size, 1, 1)
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)'
  ctx.fillStyle = 'rgba(255, 255, 255, 0.3)'
  drawMarkShape(ctx, config, mark, size, -0.5, -0.5)
  ctx.strokeStyle = textColor
  ctx.fillStyle = textColor
  drawMarkShape(ctx, config, mark, size, 0, 0)
}
function drawMarkShape(
  ctx: CanvasRenderingContext2D,
  config: MatrixConfig,
  mark: string,
  size: number,
  offsetX: number,
  offsetY: number,
) {
  const centerX = config.centerX + offsetX
  const centerY = config.centerY + offsetY
  switch (mark) {
    case 'cross':
      ctx.beginPath()
      ctx.moveTo(centerX - size, centerY)
      ctx.lineTo(centerX + size, centerY)
      ctx.moveTo(centerX, centerY - size)
      ctx.lineTo(centerX, centerY + size)
      ctx.stroke()
      break
    case 'triangle':
    case 'triangle2':
      ctx.beginPath()
      ctx.moveTo(centerX, centerY - size)
      ctx.lineTo(centerX - size, centerY + size)
      ctx.lineTo(centerX + size, centerY + size)
      ctx.closePath()
      ctx.stroke()
      break
    case 'circledot':
      ctx.beginPath()
      ctx.arc(centerX, centerY, size, 0, Math.PI * 2)
      ctx.stroke()
      ctx.beginPath()
      ctx.arc(centerX, centerY, 3, 0, Math.PI * 2)
      ctx.fill()
      break
    default:
      ctx.fillText('+', centerX, centerY)
  }
}
